import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Mail, Lock, ShieldCheck, ArrowRight, KeyRound, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const ForgotPassword = () => {
    const { forgotPassword, resetPassword } = useAuth();
    const [step, setStep] = useState('EMAIL'); // EMAIL, OTP, NEW_PASSWORD, SUCCESS
    const [email, setEmail] = useState('');
    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);

    const handleOtpChange = (element, index) => {
        if (isNaN(element.value)) return false;
        setOtp([...otp.map((d, idx) => (idx === index ? element.value : d))]);
        if (element.nextSibling) {
            element.nextSibling.focus();
        }
    };

    const handleSendCode = async () => {
        if (!email) return toast.error('Please enter your email');
        setLoading(true);
        try {
            await forgotPassword(email);
            toast.success('Verification code sent to your email');
            setStep('OTP');
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to send code');
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOtp = async () => {
        const otpString = otp.join('');
        if (otpString.length < 6) return toast.error('Please enter the full 6-digit code');
        setStep('NEW_PASSWORD');
    };

    const handleUpdatePassword = async () => {
        if (!newPassword || !confirmPassword) return toast.error('Please fill all fields');
        if (newPassword !== confirmPassword) return toast.error('Passwords do not match');
        if (newPassword.length < 6) return toast.error('Password must be at least 6 characters');

        setLoading(true);
        try {
            await resetPassword({
                email,
                otp: otp.join(''),
                newPassword
            });
            toast.success('Password updated successfully');
            setStep('SUCCESS');
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to reset password');
        } finally {
            setLoading(false);
        }
    };

    const steps = {
        EMAIL: {
            icon: <Mail size={32} />,
            title: "Reset Password",
            subtitle: "Enter your email to receive a 6-digit verification code",
            button: loading ? "Sending..." : "Send Code",
            next: handleSendCode
        },
        OTP: {
            icon: <ShieldCheck size={32} />,
            title: "Verify OTP",
            subtitle: `We've sent a code to ${email || 'your email'}`,
            button: "Verify Code",
            next: handleVerifyOtp
        },
        NEW_PASSWORD: {
            icon: <KeyRound size={32} />,
            title: "New Password",
            subtitle: "Set a strong password to secure your account",
            button: loading ? "Updating..." : "Update Password",
            next: handleUpdatePassword
        },
        SUCCESS: {
            icon: <CheckCircle2 size={32} />,
            title: "All Set!",
            subtitle: "Your password has been successfully updated.",
            button: "Back to Login",
            link: "/login"
        }
    };

    const current = steps[step];

    return (
        <div className="min-h-screen bg-white dark:bg-[#030014] pt-32 pb-20 flex items-center justify-center px-6">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="w-full max-w-md bg-slate-50 dark:bg-white/5 p-8 rounded-[2.5rem] border border-slate-100 dark:border-white/10 shadow-2xl relative overflow-hidden"
            >
                <div className="absolute top-0 right-0 w-32 h-32 bg-primary/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>

                <div className="relative z-10">
                    <div className="text-center mb-10">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-2xl text-primary mb-6">
                            {current.icon}
                        </div>
                        <h1 className="text-3xl font-black text-slate-900 dark:text-white mb-2">{current.title}</h1>
                        <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed">{current.subtitle}</p>
                    </div>

                    <AnimatePresence mode='wait'>
                        <motion.div
                            key={step}
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            className="space-y-6"
                        >
                            {step === 'EMAIL' && (
                                <div className="space-y-2">
                                    <label className="text-xs font-black uppercase tracking-widest text-primary ml-2">Email Address</label>
                                    <div className="relative group">
                                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-primary transition-colors" size={18} />
                                        <input
                                            type="email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            placeholder="name@company.com"
                                            className="w-full pl-12 pr-4 py-4 bg-white dark:bg-slate-900/50 border border-slate-200 dark:border-white/10 rounded-2xl outline-none focus:border-primary focus:ring-4 focus:ring-primary/10 transition-all dark:text-white"
                                        />
                                    </div>
                                </div>
                            )}

                            {step === 'OTP' && (
                                <div className="flex justify-between gap-2">
                                    {otp.map((data, index) => (
                                        <input
                                            key={index}
                                            type="text"
                                            maxLength="1"
                                            className="w-full h-14 text-center text-xl font-bold bg-white dark:bg-slate-900/50 border border-slate-200 dark:border-white/10 rounded-xl focus:border-primary outline-none transition-all dark:text-white"
                                            value={data}
                                            onChange={e => handleOtpChange(e.target, index)}
                                            onFocus={e => e.target.select()}
                                        />
                                    ))}
                                </div>
                            )}

                            {step === 'NEW_PASSWORD' && (
                                <>
                                    <div className="space-y-2">
                                        <label className="text-xs font-black uppercase tracking-widest text-primary ml-2">New Password</label>
                                        <div className="relative group">
                                            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                                            <input
                                                type="password"
                                                value={newPassword}
                                                onChange={(e) => setNewPassword(e.target.value)}
                                                placeholder="••••••••"
                                                className="w-full pl-12 pr-4 py-4 bg-white dark:bg-slate-900/50 border border-slate-200 dark:border-white/10 rounded-2xl outline-none focus:border-primary transition-all dark:text-white"
                                            />
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-xs font-black uppercase tracking-widest text-primary ml-2">Confirm Password</label>
                                        <div className="relative group">
                                            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                                            <input
                                                type="password"
                                                value={confirmPassword}
                                                onChange={(e) => setConfirmPassword(e.target.value)}
                                                placeholder="••••••••"
                                                className="w-full pl-12 pr-4 py-4 bg-white dark:bg-slate-900/50 border border-slate-200 dark:border-white/10 rounded-2xl outline-none focus:border-primary transition-all dark:text-white"
                                            />
                                        </div>
                                    </div>
                                </>
                            )}

                            {step === 'SUCCESS' ? (
                                <Link
                                    to="/login"
                                    className="w-full py-4 bg-primary text-white font-black rounded-2xl shadow-xl shadow-primary/20 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-2"
                                >
                                    {current.button}
                                </Link>
                            ) : (
                                <button
                                    onClick={current.next}
                                    disabled={loading}
                                    className="w-full py-4 bg-primary text-white font-black rounded-2xl shadow-xl shadow-primary/20 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-2 group disabled:opacity-50"
                                >
                                    {current.button} <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                                </button>
                            )}
                        </motion.div>
                    </AnimatePresence>

                    {step !== 'SUCCESS' && (
                        <div className="mt-8 pt-6 border-t border-slate-200 dark:border-white/10 text-center">
                            <Link to="/login" className="text-xs font-black uppercase tracking-widest text-slate-400 hover:text-primary transition-colors">
                                Back to Login
                            </Link>
                        </div>
                    )}
                </div>
            </motion.div>
        </div>
    );
};

export default ForgotPassword;
